
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Song {
  id: number;
  title: {
    de: string;
    ru: string;
  };
  category: string;
  cover: string;
  duration: string;
  inspiration: string;
  description: {
    de: string;
    ru: string;
  };
}

interface MusicPlayerProps {
  song: Song | null;
  language: 'de' | 'ru';
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  allSongs?: Song[];
  onSelectSong?: (songId: number) => void;
}

export default function MusicPlayer({ song, language, onClose, onNext, onPrevious, allSongs = [], onSelectSong }: MusicPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [showVolume, setShowVolume] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const content = {
    de: {
      nowPlaying: 'Jetzt läuft',
      inspiration: 'Inspiration',
      close: 'Schließen',
      playlist: 'Playlist',
      allSongs: 'Alle Lieder',
      search: 'Suchen...',
      noResults: 'Keine Lieder gefunden',
      heroic: 'Ruf der Tat',
      philosophical: 'Stille Wasser',
      family: 'Familie',
      romantic: 'Romantik'
    },
    ru: {
      nowPlaying: 'Сейчас играет',
      inspiration: 'Вдохновение',
      close: 'Закрыть',
      playlist: 'Плейлист',
      allSongs: 'Все песни',
      search: 'Поиск...',
      noResults: 'Песни не найдены',
      heroic: 'Зов действия',
      philosophical: 'Тихие воды',
      family: 'Семья',
      romantic: 'Романтика'
    }
  };

  const t = content[language];

  const categoryLabels: Record<string, { de: string; ru: string }> = {
    heroic: { de: 'Ruf der Tat', ru: 'Зов действия' },
    philosophical: { de: 'Stille Wasser', ru: 'Тихие воды' },
    family: { de: 'Familie', ru: 'Семья' },
    romantic: { de: 'Romantik', ru: 'Романтика' }
  };

  // Filter songs based on search query
  const filteredSongs = allSongs.filter(s => 
    s.title[language].toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.inspiration.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description[language].toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (song && audioRef.current) {
      const durationParts = song.duration.split(':');
      const totalSeconds = parseInt(durationParts[0]) * 60 + parseInt(durationParts[1]);
      setDuration(totalSeconds);
      setCurrentTime(0);
      setIsPlaying(true);
    }
  }, [song]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && currentTime < duration) {
      interval = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= duration) {
            setIsPlaying(false);
            if (onNext) {
              onNext();
            }
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTime, duration, onNext]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      setCurrentTime(Math.floor(percentage * duration));
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleSongSelect = (songId: number) => {
    if (onSelectSong) {
      onSelectSong(songId);
      setSearchQuery('');
    }
  };

  if (!song) return null;

  return (
    <AnimatePresence>
      {/* Playlist Panel */}
      {showPlaylist && (
        <motion.div
          initial={{ opacity: 0, x: 400 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 400 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-24 right-4 w-[450px] max-h-[600px] bg-gradient-to-b from-[#1e3a5f] to-[#2a4a6f] rounded-xl shadow-2xl border-2 border-[#C5B358] overflow-hidden z-50"
        >
          {/* Playlist Header */}
          <div className="p-5 border-b border-[#C5B358]/30 bg-[#1e3a5f]/50 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl font-bold text-white flex items-center">
                <i className="ri-music-2-line text-2xl mr-3 text-[#C5B358]"></i>
                {t.playlist}
              </h3>
              <button
                onClick={() => setShowPlaylist(false)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl text-white"></i>
              </button>
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-lg"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.search}
                className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 font-serif text-sm focus:outline-none focus:border-[#C5B358] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center cursor-pointer transition-all"
                >
                  <i className="ri-close-line text-xs text-white"></i>
                </button>
              )}
            </div>

            <p className="text-xs text-white/70 font-serif mt-3">
              {filteredSongs.length} {filteredSongs.length === allSongs.length ? t.allSongs : 'Ergebnisse'}
            </p>
          </div>
          
          {/* Songs List */}
          <div className="overflow-y-auto max-h-[450px] custom-scrollbar">
            {filteredSongs.length === 0 ? (
              <div className="p-8 text-center">
                <i className="ri-music-2-line text-5xl text-white/30 mb-3"></i>
                <p className="text-white/70 font-serif">{t.noResults}</p>
              </div>
            ) : (
              filteredSongs.map((s, index) => (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleSongSelect(s.id)}
                  className={`p-4 border-b border-white/10 hover:bg-white/10 transition-all cursor-pointer group ${
                    song?.id === s.id ? 'bg-white/15 border-l-4 border-l-[#C5B358]' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Album Cover */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 shadow-lg relative">
                      <img
                        src={s.cover}
                        alt={s.title[language]}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                      />
                      {song?.id === s.id && isPlaying && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <div className="flex items-center gap-1">
                            <span className="w-1 h-3 bg-[#C5B358] rounded-full animate-pulse"></span>
                            <span className="w-1 h-4 bg-[#C5B358] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></span>
                            <span className="w-1 h-3 bg-[#C5B358] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Song Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-base font-semibold text-white truncate mb-1 group-hover:text-[#C5B358] transition-colors">
                        {s.title[language]}
                      </h4>
                      <p className="text-xs text-white/70 font-serif truncate mb-1 flex items-center">
                        <i className="ri-quill-pen-line text-xs mr-1"></i>
                        {s.inspiration}
                      </p>
                      <span className="inline-block px-2 py-0.5 bg-white/10 rounded-full text-xs text-white/80 font-serif">
                        {categoryLabels[s.category]?.[language] || s.category}
                      </span>
                    </div>

                    {/* Duration & Play Icon */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <span className="text-xs text-white/50 font-mono">{s.duration}</span>
                      {song?.id === s.id ? (
                        <div className="w-8 h-8 rounded-full bg-[#8b2332] flex items-center justify-center">
                          {isPlaying ? (
                            <i className="ri-pause-fill text-white text-sm"></i>
                          ) : (
                            <i className="ri-play-fill text-white text-sm"></i>
                          )}
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-[#8b2332] flex items-center justify-center transition-all">
                          <i className="ri-play-fill text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity"></i>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-[#1e3a5f] via-[#2a4a6f] to-[#1e3a5f] shadow-2xl border-t-2 border-[#C5B358]"
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            {/* Album Cover */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="w-20 h-20 rounded-lg overflow-hidden shadow-lg">
                <img
                  src={song.cover}
                  alt={song.title[language]}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              {/* Song Info */}
              <div className="text-left">
                <p className="text-xs text-[#C5B358] font-serif mb-1">{t.nowPlaying}</p>
                <h4 className="font-serif text-lg font-bold text-white mb-1">
                  {song.title[language]}
                </h4>
                <p className="text-xs text-white/70 font-serif flex items-center">
                  <i className="ri-quill-pen-line text-sm mr-1"></i>
                  {song.inspiration}
                </p>
              </div>
            </div>

            {/* Player Controls */}
            <div className="flex-1 w-full md:w-auto">
              {/* Control Buttons */}
              <div className="flex items-center justify-center gap-4 mb-3">
                <button
                  onClick={onPrevious}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all cursor-pointer"
                  disabled={!onPrevious}
                >
                  <i className="ri-skip-back-fill text-xl text-white"></i>
                </button>

                <button
                  onClick={togglePlay}
                  className="w-14 h-14 rounded-full bg-[#8b2332] hover:bg-[#6d1a27] border-2 border-[#C5B358] flex items-center justify-center transition-all shadow-lg hover:shadow-xl cursor-pointer"
                >
                  {isPlaying ? (
                    <i className="ri-pause-fill text-3xl text-white"></i>
                  ) : (
                    <i className="ri-play-fill text-3xl text-white ml-1"></i>
                  )}
                </button>

                <button
                  onClick={onNext}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all cursor-pointer"
                  disabled={!onNext}
                >
                  <i className="ri-skip-forward-fill text-xl text-white"></i>
                </button>
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-white/70 font-mono w-10 text-right">
                  {formatTime(currentTime)}
                </span>
                <div
                  ref={progressRef}
                  onClick={handleProgressClick}
                  className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden cursor-pointer group"
                >
                  <div
                    className="h-full bg-gradient-to-r from-[#C5B358] to-[#8b2332] transition-all duration-300 relative"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                </div>
                <span className="text-xs text-white/70 font-mono w-10">
                  {formatTime(duration)}
                </span>
              </div>
            </div>

            {/* Volume, Playlist & Close */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Playlist Button */}
              <button
                onClick={() => setShowPlaylist(!showPlaylist)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer relative ${
                  showPlaylist ? 'bg-[#8b2332]' : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                <i className="ri-list-check text-xl text-white"></i>
                {allSongs.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C5B358] rounded-full text-xs text-[#1e3a5f] font-bold flex items-center justify-center">
                    {allSongs.length}
                  </span>
                )}
              </button>

              {/* Volume Control */}
              <div className="relative">
                <button
                  onClick={() => setShowVolume(!showVolume)}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all cursor-pointer"
                >
                  {volume === 0 ? (
                    <i className="ri-volume-mute-fill text-xl text-white"></i>
                  ) : volume < 0.5 ? (
                    <i className="ri-volume-down-fill text-xl text-white"></i>
                  ) : (
                    <i className="ri-volume-up-fill text-xl text-white"></i>
                  )}
                </button>

                {showVolume && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#1e3a5f] p-3 rounded-lg shadow-xl"
                  >
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="w-24 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#C5B358] [&::-webkit-slider-thumb]:cursor-pointer"
                      style={{ writingMode: 'bt-lr', transform: 'rotate(-90deg)' }}
                    />
                  </motion.div>
                )}
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl text-white"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} className="hidden" />
      </motion.div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(197, 179, 88, 0.5);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(197, 179, 88, 0.7);
        }
      `}</style>
    </AnimatePresence>
  );
}
